"use client"
import React, { useState, useEffect, useRef } from "react"
import { motion, useInView } from "framer-motion"
import { BarChart3, Printer, Car, CreditCard, Smartphone, Users, TrendingUp, Building2, Zap, ArrowRight, Play, AlertTriangle, DollarSign, ClipboardList, XCircle, Frown, CheckCircle2, LineChart, Smile, ChevronRight } from "lucide-react"
import heroVideo from "../../assets/Video_De_Coche_Con_Luces.mp4"

/* ── Counter hook ── */
function useCountUp(end, duration = 2000, startOnView = true, inView = true) {
  const [count, setCount] = useState(0)
  useEffect(() => {
    if (!startOnView || !inView) return
    let start = 0
    const step = Math.ceil(end / (duration / 16))
    const timer = setInterval(() => {
      start += step
      if (start >= end) { setCount(end); clearInterval(timer) }
      else setCount(start)
    }, 16)
    return () => clearInterval(timer)
  }, [end, duration, startOnView, inView])
  return count
}

// Feature Card Component
const FeatureCard = ({ icon, title, description, badge, impact, featured, index, inView }) => (
  <motion.div
    className={`feat-card${featured ? " feat-card--featured" : ""}`}
    initial={{ opacity: 0, y: 40 }}
    animate={inView ? { opacity: 1, y: 0 } : {}}
    transition={{ duration: 0.5, delay: 0.12 * index, ease: "easeOut" }}
  >
    {/* Hover shimmer overlay */}
    <div className="feat-card-shimmer" />
    {badge && <span className="feat-card-badge">{badge}</span>}
    <div className="feat-card-icon">{icon}</div>
    {impact && <span className="feat-card-impact">{impact}</span>}
    <h3 className="feat-card-title">{title}</h3>
    <p className="feat-card-desc">{description}</p>
  </motion.div>
)

/* ── Animated Stat Card ── */
const StatCard = ({ icon, value, suffix, label, delay, inView }) => {
  const count = useCountUp(value, 2200, true, inView)
  return (
    <motion.div
      className="hero-stat-card"
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
    >
      <div className="hero-stat-icon">{icon}</div>
      <span className="hero-stat-number">{suffix === "%" ? `+${count}%` : suffix === "h" ? `<${value}h` : `+${count}`}</span>
      <span className="hero-stat-label">{label}</span>
    </motion.div>
  )
}

/* ── Hero ── */
const Hero = ({ handleNavClick }) => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: false, amount: 0.4 })
  const [showVideo, setShowVideo] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setShowVideo(true), 2000)
    return () => clearTimeout(timer)
  }, [])

  return (
    <section className="hero-section-ini" ref={ref}>
      {/* Video Background */}
      <div className="hero-video-container">
        <video
          autoPlay
          muted
          loop
          playsInline
          className={`hero-video-bg ${showVideo ? "hero-video-visible" : ""}`}
        >
          <source src={heroVideo} type="video/mp4" />
        </video>
        <div className="hero-video-overlay" />
      </div>
      {/* Animated grid background */}
      <div className="hero-bg-grid" />
      <div className="hero-bg-glow hero-bg-glow--1" />
      <div className="hero-bg-glow hero-bg-glow--2" />

      <div className="container-ini">
        <div className="hero-content-ini">



          {/* Title */}
          <motion.h1
            className="hero-title-ini"
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.25, ease: "easeOut" }}
          >
            ¿Listo para transformar tu parqueadero en un{" "}
            negocio inteligente?
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            className="hero-description-ini"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.45 }}
          >
            Aumenta tus ingresos, toma el control total de tu operación y automatiza
            tu parqueadero en menos de 24 horas. Sin complicaciones, sin contratos largos.
          </motion.p>

          {/* Buttons */}
          <motion.div
            className="button-group-ini"
            initial={{ opacity: 0, y: 25 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.55, delay: 0.6 }}
          >
            <button className="secondary-button-ini notranslate" translate="no" onClick={() => handleNavClick("contactanos")}>
              Solicitar Informacion <ArrowRight size={18} />
            </button>
          </motion.div>

          {/* Stats */}
          <div className="hero-stats-row">
            <StatCard icon={<Building2 size={20} />} value={120} suffix="+" label="Parqueaderos activos" delay={0.75} inView={inView} />
            <StatCard icon={<TrendingUp size={20} />} value={35} suffix="%" label="Aumento promedio en ingresos" delay={0.9} inView={inView} />
            <StatCard icon={<Zap size={20} />} value={24} suffix="h" label="Implementación rápida" delay={1.05} inView={inView} />
          </div>
        </div>
      </div>
    </section>
  )
}

/* ── Before vs After ── */
const problems = [
  { icon: <AlertTriangle size={18} />, text: "Desorden en el control de vehículos" },
  { icon: <DollarSign size={18} />, text: "Pérdidas de dinero sin detectar" },
  { icon: <ClipboardList size={18} />, text: "Control manual con papel y lápiz" },
  { icon: <XCircle size={18} />, text: "Errores constantes en cobros" },
  { icon: <Frown size={18} />, text: "Clientes molestos por mal servicio" },
]

const solutions = [
  { icon: <CheckCircle2 size={18} />, text: "Aumento de ingresos comprobado" },
  { icon: <LineChart size={18} />, text: "Control en tiempo real" },
  { icon: <BarChart3 size={18} />, text: "Reportes automáticos al instante" },
  { icon: <Car size={18} />, text: "Optimización inteligente de espacios" },
  { icon: <Smile size={18} />, text: "Experiencia premium para clientes" },
]

const BeforeAfter = ({ handleNavClick }) => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: false, amount: 0.4 })

  return (
    <section className="ba-section" ref={ref}>
      <div className="ba-grid">
        {/* LEFT — Problems */}
        <motion.div
          className="ba-col ba-col--left"
          initial={{ opacity: 0, x: -40 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <span className="ba-label ba-label--problem">
            <Frown size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} /> Antes
          </span>
          <h3 className="ba-col-title ba-col-title--problem">¿Tu parqueadero aún funciona así?</h3>
          <ul className="ba-list">
            {problems.map((p, i) => (
              <motion.li
                key={i}
                className="ba-item ba-item--problem"
                initial={{ opacity: 0, x: -20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.35, delay: 0.2 + i * 0.08 }}
              >
                <span className="ba-item-icon ba-item-icon--problem">{p.icon}</span>
                <span className="ba-item-text">{p.text}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>

        {/* CENTER — Quote divider */}
        <motion.div
          className="ba-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="ba-center-line" />
          <p className="ba-quote">
            La diferencia entre <strong>administrar…</strong> y <strong>liderar</strong> tu negocio.
          </p>
          <div className="ba-center-line" />
        </motion.div>

        {/* RIGHT — Solutions */}
        <motion.div
          className="ba-col ba-col--right"
          initial={{ opacity: 0, x: 40 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
        >
          <span className="ba-label ba-label--solution">
            <Smile size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} /> Con Parking Club
          </span>
          <h3 className="ba-col-title ba-col-title--solution">Tu negocio automatizado</h3>
          <ul className="ba-list">
            {solutions.map((s, i) => (
              <motion.li
                key={i}
                className="ba-item ba-item--solution"
                initial={{ opacity: 0, x: 20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.35, delay: 0.25 + i * 0.08 }}
              >
                <span className="ba-item-icon ba-item-icon--solution">{s.icon}</span>
                <span className="ba-item-text">{s.text}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>
      </div>

      {/* Bottom CTA */}
      <motion.div
        className="ba-bottom-cta"
        initial={{ opacity: 0, y: 15 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, delay: 0.7 }}
      >
        <button className="ba-cta" onClick={() => handleNavClick("servicios")}>
          Descubre cómo funciona <ChevronRight size={18} />
        </button>
      </motion.div>
    </section>
  )
}

// Subcomponente Features — Premium SaaS
const Features = ({ handleNavClick }) => {
  const sectionRef = useRef(null)
  const inView = useInView(sectionRef, { once: true, margin: "-80px" })

  const features = [
    {
      icon: <BarChart3 size={26} />,
      title: "Control financiero",
      description: "Monitorea ingresos, genera reportes detallados y analiza el rendimiento financiero de cada sucursal en tiempo real.",
      badge: "Más usado",
      impact: "Control total",
    },
    {
      icon: <Printer size={26} />,
      title: "Impresión de tickets",
      description: "Tickets automáticos con QR para agilizar entradas y salidas sin filas ni demoras.",
      badge: "Automático",
      impact: "Cobros sin errores",
    },
    {
      icon: <Car size={26} />,
      title: "Gestión de espacios",
      description: "Visualiza la ocupación en vivo y optimiza la distribución de vehículos de forma inteligente.",
      badge: "En tiempo real",
      impact: "Optimiza espacios",
    },
    {
      icon: <CreditCard size={26} />,
      title: "Control de pagos",
      description: "Acepta efectivo, transferencias y múltiples métodos con conciliación automática.",
      impact: "Pagos seguros",
    },
    {
      icon: <Smartphone size={26} />,
      title: "App móvil",
      description: "Gestiona tu parqueadero desde cualquier lugar con nuestra app nativa e intuitiva.",
      impact: "Desde tu bolsillo",
    },
    {
      icon: <Users size={26} />,
      title: "Fidelización",
      description: "Programas de lealtad, membresías y tarifas VIP para clientes frecuentes.",
      impact: "Clientes felices",
    },
  ]

  return (
    <section className="feat-section" ref={sectionRef}>
      {/* Background grid pattern */}
      <div className="feat-bg-grid" />

      <div className="container-ini">
        {/* Section header */}
        <motion.div
          className="feat-header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="feat-badge-section">Características principales</span>
          <div className="feat-title-wrap">
            <div className="feat-title-glow" />
            <h2 className="feat-title">Todo lo que tu parqueadero necesita</h2>
          </div>
          <p className="feat-subtitle">Una plataforma, cero complicaciones.</p>
        </motion.div>

        {/* Grid */}
        <div className="feat-grid">
          {features.map((f, i) => (
            <FeatureCard
              key={i}
              index={i}
              icon={f.icon}
              title={f.title}
              description={f.description}
              badge={f.badge}
              impact={f.impact}
              featured={f.featured}
              inView={inView}
            />
          ))}
        </div>


      </div>
    </section>
  )
}

// Subcomponente CTA
const CTA = ({ handleNavClick }) => (
  <section className="cta-section-ini">
    <div className="container-ini">
      <div className="cta-card-ini">
        <div className="cta-content-ini">
          <h2 className="cta-title-ini">¿Listo para optimizar la gestión de tu parqueadero?</h2>
          <p className="cta-description-ini">
            Únete a más de 100 empresas que ya confían en Parking Club para la gestión de sus estacionamientos.
          </p>
        </div>
        <div>
          <button className="cta-button-ini" onClick={() => handleNavClick("contactanos")}>
            Solicitar informacion
          </button>
        </div>
      </div>
    </div>
  </section>
)

// Componente principal InicioSection
export default function InicioSection({ handleNavClick, onThemeChange }) {
  const heroRef = useRef(null)
  const baRef = useRef(null)
  const featRef = useRef(null)
  const ctaRef = useRef(null)

  useEffect(() => {
    if (!onThemeChange) return

    const themeMap = {
      hero: "hero",
      ba: "dark",
      features: "hero",
      cta: "light",
    }

    const refs = [
      { ref: heroRef, name: "hero" },
      { ref: baRef, name: "ba" },
      { ref: featRef, name: "features" },
      { ref: ctaRef, name: "cta" },
    ]

    const ratios = {}

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const name = entry.target.dataset.section
          ratios[name] = entry.intersectionRatio
        })

        // Pick the section with highest visibility
        let best = null
        let bestRatio = 0
        for (const [name, ratio] of Object.entries(ratios)) {
          if (ratio > bestRatio) {
            bestRatio = ratio
            best = name
          }
        }
        if (best && themeMap[best]) {
          onThemeChange(themeMap[best])
        }
      },
      { threshold: [0, 0.15, 0.3, 0.5, 0.7, 1] }
    )

    refs.forEach(({ ref }) => {
      if (ref.current) observer.observe(ref.current)
    })

    return () => observer.disconnect()
  }, [onThemeChange])

  return (
    <div className="unified-background-ini">
      <div ref={heroRef} data-section="hero">
        <Hero handleNavClick={handleNavClick} />
      </div>
      <div ref={baRef} data-section="ba">
        <BeforeAfter handleNavClick={handleNavClick} />
      </div>
      <div ref={featRef} data-section="features">
        <Features handleNavClick={handleNavClick} />
      </div>
      <div ref={ctaRef} data-section="cta">
        <CTA handleNavClick={handleNavClick} />
      </div>
    </div>
  )
}
